import type * as React from 'react';

import type { IOrderItem, IInsuranceOrderItem, IItemsType, IResort, ICurrency } from './types';
import { calculateItemPrice } from './helpers';
import t from './locales';

import OrderItemView from './OrderItemView';
import OrderItemPriceView from './OrderItemPriceView';
import OrderItemNumberSelect from './OrderItemNumberSelect';
import Price from '../Shared/Price';
import { isAuItem } from '../utils/commonHelper';
import { getLocale } from '../utils/pathHelper';
import { formatDateWithDayOfWeek } from '../utils/dateHelper';

interface IOrderItemProps {
  item: IOrderItem|IInsuranceOrderItem;
  itemsType: IItemsType;
  handleChange: (event: React.ChangeEvent) => void;
  handleRemove: (evnet: React.MouseEvent) => void;
  index: number;
  resort?: IResort;
  localCurrency?: ICurrency;
  charge?: boolean;
  axess?: boolean;
}

const OrderItem: React.FC<IOrderItemProps> = ({ item, itemsType, handleChange, handleRemove, index, localCurrency, charge, axess }) => {
  const { resort, itemName, checkInDate, checkOutDate, price, people, maxParticipants, timeSchedule, deposit, itemId } = item;
  const setProduct = item.type === 'Order' ? item.setProduct : undefined;
  const adultLabel = setProduct ? t.setProductNum() : t.adultNum();
  const isKanjiwari = item.type === "Order" ? item.isKanjiwari : false;
  const isCharge = item.type === "Order" ? item.isCharge : false;
  const forAxess = item.type === "Order" ? item.forAxess : false;
  const isInsuranceOrder = itemsType === 'insuranceOrderItems';
  const { adultPrice, childPrice, groupPrice, studentPrice, seniorPrice } = price;
  const itemPrice = calculateItemPrice(price, people);
  const numberSelectClass = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ? "number-select-sp" : "number-select-pc"
  const locale: string = getLocale() || 'en';

  const OrderItemPrice = () => {
    return (
      <OrderItemPriceView>
        <div className='item-price'>
          {itemPrice.price === itemPrice.fullPrice ?
            <Price
              price={itemPrice.price}
              fontSize={14}
              localCurrency={localCurrency} /> :
            <Price
              price={itemPrice.price}
              fullPrice={itemPrice.fullPrice}
              fontSize={14}
              showDiscount
              localCurrency={localCurrency} />}
          {deposit > 0 &&
            <div className="d-flex align-items-center mt-2">
              <span className="small text-muted mr-2">{t.icCardDeposit()}:</span>
              <Price
                price={(people.adultNum + people.childNum + people.studentNum + people.seniorNum) * deposit}
                fontSize={12}
                localCurrency={localCurrency} />
            </div>}
          {locale !== 'ja' &&
            <a href="#" onClick={handleRemove} data-index={index} data-items-type={itemsType} className="trash">
              <i className="fas fa-trash" />
            </a>
          }
        </div>

        { locale === 'ja' &&
          <a href="#" onClick={handleRemove} data-index={index} data-items-type={itemsType} className="remove-item">
            <i className="fas fa-trash" />
            カートから削除
          </a>
        }
      </OrderItemPriceView>
    )
  }

  return (
    <OrderItemView key={`order-item-${index}`}>
      <div>
        <p className="name">
          {itemName}
        </p>
        {resort &&
          <p className="mute">
            {resort.resortName}
          </p>}
        {checkInDate && checkOutDate &&
          <p className="date">
            {t.dateOfUse()}：
            {formatDateWithDayOfWeek(checkInDate)}
            {checkInDate !== checkOutDate && <span> - {formatDateWithDayOfWeek(checkOutDate)}</span>}
            {timeSchedule != null ? <span> {timeSchedule}</span> : ''}
          </p>}
        <div className={numberSelectClass}>
          {adultPrice && isAuItem() && !isKanjiwari && !isCharge && !isInsuranceOrder &&
            <div>
              <p>Quantity</p>
              <OrderItemNumberSelect
                selectedNumber={people.adultNum}
                handleChange={handleChange}
                itemsType={itemsType}
                propKey="adultNum"
                index={index}
                max={maxParticipants} />
            </div>}
          {(adultPrice || groupPrice) && !isKanjiwari && !isInsuranceOrder && !isCharge && !isAuItem() &&
            <div>
              <p>{adultLabel}</p>
              <OrderItemNumberSelect
                selectedNumber={people.adultNum}
                handleChange={handleChange}
                itemsType={itemsType}
                propKey="adultNum"
                index={index}
                max={maxParticipants} />
            </div>}
          {!setProduct && (childPrice || groupPrice) && !isKanjiwari && !isCharge && !isInsuranceOrder && !isAuItem() &&
            <div>
              <p>{t.childNum()}</p>
              <OrderItemNumberSelect
                selectedNumber={people.childNum}
                handleChange={handleChange}
                itemsType={itemsType}
                propKey="childNum"
                index={index}
                max={maxParticipants} />
            </div>}
          {!setProduct && (studentPrice || groupPrice) && !isKanjiwari && !isCharge && !isInsuranceOrder && !isAuItem() &&
            <div>
              <p>{t.studentNum()}</p>
              <OrderItemNumberSelect
                selectedNumber={people.studentNum}
                handleChange={handleChange}
                itemsType={itemsType}
                propKey="studentNum"
                index={index}
                max={maxParticipants} />
            </div>}
          {!setProduct && (seniorPrice || groupPrice) && !isKanjiwari && !isCharge && !isInsuranceOrder && !isAuItem() &&
            <div>
              <p>{t.seniorNum()}</p>
              <OrderItemNumberSelect
                selectedNumber={people.seniorNum}
                handleChange={handleChange}
                itemsType={itemsType}
                propKey="seniorNum"
                index={index}
                max={maxParticipants} />
            </div>}
          {
            groupPrice && isKanjiwari &&
            <div>
              <p>{adultLabel}:{people.adultNum}</p>
            </div>}
          {
            ((adultPrice && isCharge) || (adultPrice && forAxess)) &&
            <div>
              <p>{adultLabel}:{people.adultNum}</p>
            </div>}
          {
            !setProduct && ((childPrice && isCharge) || (childPrice && forAxess)) &&
            <div>
              <p>{t.childNum()}:{people.childNum}</p>
            </div>}
          {
            !setProduct && ((studentPrice && isCharge) || (studentPrice && forAxess)) &&
            <div>
              <p>{t.studentNum()}:{people.studentNum}</p>
            </div>}
          {
            !setProduct && ((seniorPrice && isCharge) || (seniorPrice && forAxess)) &&
            <div>
              <p>{t.seniorNum()}:{people.seniorNum}</p>
            </div>}
          {adultPrice && isInsuranceOrder &&
          <div>
            <p>{t.peopleNum()}</p>
            <OrderItemNumberSelect
              selectedNumber={people.adultNum}
              handleChange={handleChange}
              itemsType={itemsType}
              propKey="adultNum"
              index={index}
              max={maxParticipants} />
          </div>}
        </div>
      </div>
      <OrderItemPrice />
    </OrderItemView>
  );
}

export default OrderItem;
